import { useCallback, useEffect, useMemo } from "react";
import {
  alpha,
  Box,
  Button,
  Skeleton,
  Stack,
  styled,
  Tooltip,
} from "@mui/material";
import { styles, commonStyles } from "./style";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import splitIcon from "../../../assets/images/icons/viewerWrapper/split.png";
import fullscrennIcon from "../../../assets/images/icons/viewerWrapper/fullscreen.png";
import { Close } from "@mui/icons-material";
import Dropdown from "../Dropdown";
import { useLoading } from "contexts/LoadingContext";
import { useAps, APS_SCOPES } from "contexts/ApsContext";
import { useWorkspaces } from "contexts/WorkspacesContext";
import APSLogo from "../../../assets/images/aps.png";
import CONFIG from "utils/config";
import { useAtom } from "jotai";
import apsModelSettingsPanelVisibleAtom from "store/atoms/apsViewer/apsModelSettingsPanelVisibleAtom";
import { Icon } from "@iconify/react";
import { useGetOutputPointcloud } from "api/output/queries/useGetOutputPointcloud";

// Define APS redirect URL based on current window location
const APS_REDIRECT_URL = `${window.location.origin}/settings/integrations`;

interface SearchParams {
  type: "vtour" | "ptcld";
  mode: "uniscan" | "uniscan-compare" | "biscan";
}

const CustomButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.common.white, 0.05),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  color: theme.palette.common.white,
  borderRadius: 10,
  px: 2,
  py: 1,
  textTransform: "none",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.12),
  },
  "&.Mui-disabled": {
    backgroundColor: "background.default",
  },
}));

const uniscanOptions = [
  { id: "ptcld", label: "Point Cloud" },
  { id: "vtour", label: "Virtual Tour" },
];

const Toolbar = ({
  isOverlap = false,
  loading = false,
}: {
  isOverlap?: boolean;
  loading?: boolean;
}) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const mode = (queryParams.get("mode") as SearchParams["mode"]) || "uniscan";
  const type = (queryParams.get("type") as SearchParams["type"]) || "vtour";
  const { fullscreen, setFullscreen, parentRef } = useLoading();
  const { loading: loadingAps, session } = useAps();
  const { activeWorkspace } = useWorkspaces();
  const location = useLocation();
  const [modelSettingsPanelVisible, setModelSettingsPanelVisible] = useAtom(apsModelSettingsPanelVisibleAtom);
  const isPublic = useMemo(() => {
    return location.pathname.startsWith("/public");
  }, [location.pathname]);

  const {
    loading: pointcloudLoading,
    setLoading: setPointcloudLoading,
    setProgress: setLoadingProgression,
    progress: loadingProgression,
    blurryEffect,
    setBlurryEffect,
    ptcldOutputId1: firstOutputId,
    ptcldOutputId2: secondOutputId,
    vtourOutputId1,
    scan1,
    scan2,
    scan1Data,
  } = useLoading();

  const {
    data: pointCloudData,
    isLoading: dataLoading,
    error: dataError,
  } = useGetOutputPointcloud(firstOutputId);

  const {
    data: pointCloudData2,
    isLoading: dataLoading2,
    error: dataError2,
  } = useGetOutputPointcloud(secondOutputId);

  const compareOptions: {id: string; label: string}[] = useMemo(() => {
    const list = [
      { id: "ptcld-ptcld", label: "Point Cloud" },
      { id: "vtour-vtour", label: "Virtual Tour" },
      { id: "ptcld-vtour", label: "Virtual Tour | Point Cloud" },
    ]

    if (pointCloudData2?.ifc) {
      list.push({
        id: "ptcld-ifc",
        label: "Point Cloud | IFC"
      })
    }

    return list
  }, [pointCloudData2])

  const toggleFullScreen = useCallback(() => {
    if (parentRef.current) {
      if (!fullscreen) {
        parentRef.current.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  }, [fullscreen, setFullscreen, parentRef]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [setFullscreen]);

  const typeMode = useCallback(
    (newMode: SearchParams["mode"]) => {
      const params = new URLSearchParams(queryParams);
      params.set("mode", newMode);
      params.delete("selection");
      setQueryParams(params, { replace: true });
    },
    [queryParams, setQueryParams]
  );
  const typeChange = useCallback(
    (newType: SearchParams["type"]) => {
      const params = new URLSearchParams(queryParams);
      params.set("type", newType);
      setQueryParams(params, { replace: true });
    },
    [queryParams, setQueryParams]
  );

  const updateTypeAndMode = useCallback(
    (newType: SearchParams["type"], newMode: SearchParams["mode"], secondType?: SearchParams["type"] | 'ifc') => {
      const params = new URLSearchParams(queryParams);
      params.set("type", newType);
      params.set("mode", newMode);
      if (secondType) {
        params.set("secondType", secondType);
      } else {
        params.delete("secondType");
      }
      setQueryParams(params, { replace: true });
    },
    [queryParams, setQueryParams]
  );

  const onBiScanChange = useCallback(
    (option: { id: string; label: string }) => {
      if (option.id === "ptcld-ptcld") {
        updateTypeAndMode("ptcld", "biscan");
      } else if (option.id === "vtour-vtour") {
        updateTypeAndMode("vtour", "biscan");
      } else if (option.id === "ptcld-vtour") {
        updateTypeAndMode("ptcld", "uniscan-compare");
      } else if (option.id === "ptcld-ifc") {
        updateTypeAndMode("ptcld", "uniscan-compare", "ifc");
      }
    },
    [updateTypeAndMode]
  );

  const onUniScanChange = useCallback(
    (option: { id: string; label: string }) => {
      const params = new URLSearchParams(queryParams);
      params.delete("secondType");
      if (option.id === "ptcld") {
        params.set("type", "ptcld");
      } else if (option.id === "vtour") {
        params.set("type", "vtour");
      }
      setQueryParams(params, { replace: true });
    },
    [queryParams, setQueryParams]
  );

  const APS_REDIRECT_URL = "http://localhost:3000/oauth/aps/callback";


  if (loading) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={(theme) => ({
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
          p: 1,
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.8),
          backdropFilter: "blur(10px)",
          boxShadow: (theme) => theme.shadows[2],
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          ...(fullscreen && type === "vtour"
            ? {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }
            : {}),
        })}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Skeleton
            variant="circular"
            width={32}
            height={32}
            sx={{ backgroundColor: "primary.main" }}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Skeleton
            variant="rectangular"
            width={100}
            height={36}
            sx={{ borderRadius: 2, backgroundColor: "primary.main" }}
          />
          <Skeleton
            variant="circular"
            width={36}
            height={36}
            sx={{ borderRadius: 2, backgroundColor: "primary.main" }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Skeleton
            variant="circular"
            width={36}
            height={36}
            sx={{ borderRadius: 2, backgroundColor: "primary.main" }}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={(theme) => ({
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        p: 1,
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
        backdropFilter: "blur(10px)",
        boxShadow: (theme) => theme.shadows[2],
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        ...(isOverlap
          ? {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
          }
          : {}),
      })}
    >
      <Stack direction="row" alignItems="center" gap={1} pl={1}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: alpha("#ffffff", 0.05),
            backdropFilter: "blur(10px)",
            border: `1px solid ${alpha("#ffffff", 0.12)}`,
            display: mode !== "uniscan" ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "background.paper",
            },
          }}
          onClick={() => typeMode(mode === "uniscan" ? "biscan" : "uniscan")}
        >
          <Close sx={{ color: "white" }} fontSize="small" />
        </Box>
      </Stack>

      <Stack direction="row" spacing={2}>
        {mode === "uniscan" ? (
          <Dropdown
            key={`${mode}-${type}`}
            options={uniscanOptions}
            defaultSelectedId={type}
            onChange={onUniScanChange}
            buttonText="View Mode"
          />
        ) : (
          <Dropdown
            key={`${mode}-${type}`}
            options={compareOptions}
            defaultSelectedId={
              mode === "uniscan-compare" 
                ? queryParams.get("secondType") === "ifc" 
                  ? "ptcld-ifc" 
                  : "ptcld-vtour" 
                : `${type}-${type}`
            }
            onChange={onBiScanChange}
            buttonText="View Mode"
          />
        )}
        <Tooltip
          title="Compare Scans"
          placement="top"
          PopperProps={{
            style: {
              zIndex: 10,
            },
          }}
        >
          <Stack
            sx={commonStyles(mode !== "uniscan")}
            onClick={() => typeMode(mode === "uniscan" ? "biscan" : "uniscan")}
          >
            <img src={splitIcon} alt="splitIcon" width="55%" height="55%" />
          </Stack>
        </Tooltip>
        {!isPublic && activeWorkspace?.featureFlags?.includes("aps-integration") && (
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {session ? (
              <Box
                component={"img"}
                src={APSLogo}
                alt={"APS Logo"}
                sx={{
                  height: "2rem",
                }}
              />
            ) : (
              <Link to={`https://developer.api.autodesk.com/authentication/v1/authorize?response_type=code&client_id=${CONFIG.APS_CLIENT_ID}&redirect_uri=${encodeURIComponent(APS_REDIRECT_URL)}&scope=${encodeURIComponent(APS_SCOPES)}`}>
                <Button>{loadingAps ? "Loading" : "Connect with APS"}</Button>
              </Link>
            )}
          </Box>
        )}
      </Stack>

      <Stack sx={styles.toolbarminiWrapper}>
        {/* Show transform controls button only when type=vtour and secondType=aps */}
        {type === "vtour" && queryParams.get("secondType") === "aps" && (
          <Tooltip
            title="Transform Controls"
            placement="top"
            PopperProps={{
              style: {
                zIndex: 10,
              },
            }}
          >
            <Stack
              sx={commonStyles(modelSettingsPanelVisible)}
              onClick={() => setModelSettingsPanelVisible(!modelSettingsPanelVisible)}
            >
              <Icon
                icon="material-symbols:tune"
                width="60%"
                height="60%"
                color="white"
              />
            </Stack>
          </Tooltip>
        )}
        <Tooltip
          title="Fullscreen"
          placement="top"
          PopperProps={{
            style: {
              zIndex: 10,
            },
          }}
        >
          <Stack sx={commonStyles(fullscreen)} onClick={toggleFullScreen}>
            <img
              src={fullscrennIcon}
              alt="fullscreenIcon"
              width="60%"
              height="60%"
            />
          </Stack>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default Toolbar;
